/**
 * Выбор одного значения из закрытого списка.
 *
 * Почему не нативный `<select>`. Три причины, и все три видны на этом
 * продукте, а не в общих рассуждениях:
 *
 * * **он раздувается до самой длинной опции.** Код замечания качества вроде
 *   `CONFLICT_RESOLUTION_ZERO_AREA_NOT_CARRIED` выталкивал за край весь экран,
 *   и страница начинала ездить вбок (`shell.module.css:242-263`). Здесь ширину
 *   задаёт полоса фильтров, а длинная подпись обрезается многоточием;
 * * **его нельзя одеть.** Список рисует операционная система: ни токенов, ни
 *   подписи «чем этот вариант отличается», ни знака выбора — и на разных
 *   машинах он выглядит по-разному;
 * * **в нём нет второй строки.** Варианты вроде «Фермерские (вид по
 *   умолчанию)» приходится объяснять прямо в подписи, и подпись становится
 *   предложением.
 *
 * Поведение целиком принадлежит React Aria: роль `listbox`, стрелки, Home/End,
 * набор первых букв, Escape, возврат фокуса на триггер, чтение вслух.
 * Собственной клавиатуры здесь нет и быть не должно.
 *
 * Пустое значение. Реестры показывают «не важно» пустой строкой в URL, но
 * ключом коллекции пустая строка быть не может. Поэтому она разворачивается в
 * служебный ключ на входе и сворачивается обратно на выходе — граница
 * проходит по компоненту, и наружу продолжает ездить та же пустая строка.
 */

import { Check, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
  Text,
} from "react-aria-components";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { Explained, FIELD_TEXT } from "./InfoPopover";
import styles from "./controls.module.css";

const m = defineMessages({
  notChosen: { id: "common.kit.select.notChosen", defaultMessage: "не выбрано", description: "Подсказка внутри выпадающего списка, пока ничего не выбрано; строчная, коротко" },
});

export interface ChoiceOption<V extends string = string> {
  value: V;
  label: string;
  /** Короткая подпись справа: чем этот вариант отличается от соседнего. */
  hint?: string;
  isDisabled?: boolean;
}

/** Пустая строка ключом коллекции быть не может — «не важно» получает свой. */
const EMPTY_KEY = "__empty__";

const toKey = (value: string) => (value === "" ? EMPTY_KEY : value);
const fromKey = (key: string) => (key === EMPTY_KEY ? "" : key);

export interface SelectProps<V extends string = string> {
  /** Видимая подпись. Без неё обязателен `aria-label`. */
  label?: ReactNode;
  "aria-label"?: string;
  /** Объяснение подписи. Открывается нажатием, а не наведением. */
  explain?: ReactNode;
  options: readonly ChoiceOption<V>[];
  /** Пустая строка — «ничего не выбрано» либо «не важно». */
  value: V | "";
  onChange: (value: V | "") => void;
  /** Что показать, когда выбранного значения нет в списке. */
  placeholder?: string;
  /** Строка под полем: ограничение выборки, а не повтор подписи. */
  description?: ReactNode;
  isDisabled?: boolean;
  size?: "sm" | "md";
  className?: string;
}

export function Select<V extends string = string>({
  label,
  "aria-label": ariaLabel,
  explain,
  options,
  value,
  onChange,
  placeholder,
  description,
  isDisabled,
  size = "md",
  className,
}: SelectProps<V>) {
  const t = useT();
  const items = options.map((option) => ({
    ...option,
    id: toKey(option.value),
  }));

  return (
    <AriaSelect
      className={`${styles.field} ${size === "sm" ? styles.sizeSm : ""} ${className ?? ""}`}
      selectedKey={toKey(value)}
      isDisabled={isDisabled}
      placeholder={placeholder ?? t(m.notChosen)}
      aria-label={label ? undefined : ariaLabel}
      onSelectionChange={(key) => onChange(fromKey(String(key)) as V | "")}
    >
      {label ? (
        <Label className={styles.label}>
          <Explained
            label={typeof label === "string" ? label : (ariaLabel ?? t(FIELD_TEXT.valueFallback))}
            explain={explain}
          >
            {label}
          </Explained>
        </Label>
      ) : null}

      <Button className={styles.trigger}>
        {/* В триггере стоит только ТЕКСТ выбранного, а не вся строка списка:
            иначе туда переезжают и знак выбора, и подпись «чем отличается» —
            и слипаются в «Фермерскиевид по умолчанию». */}
        <SelectValue className={styles.triggerValue}>
          {({ isPlaceholder, selectedText, defaultChildren }) =>
            isPlaceholder ? defaultChildren : selectedText}
        </SelectValue>
        <ChevronDown
          className={styles.triggerIcon}
          size={16}
          strokeWidth={1.75}
          aria-hidden
        />
      </Button>

      {description ? (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      ) : null}

      <Popover className={styles.popover}>
        <ListBox className={styles.listbox} items={items}>
          {(option) => (
            <ListBoxItem
              id={option.id}
              className={styles.option}
              textValue={option.label}
              isDisabled={option.isDisabled}
            >
              {({ isSelected }) => (
                <>
                  {/* Знак выбора, а не только фон: цвет не имеет права быть
                      единственным носителем смысла. */}
                  <span className={styles.optionMark} aria-hidden>
                    {isSelected ? <Check size={14} strokeWidth={2.25} /> : null}
                  </span>
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.hint ? (
                    <span className={styles.optionHint}>{option.hint}</span>
                  ) : null}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
